import type { ChangeEvent, Ref } from "react";
import { forwardRef, memo, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { mergeRefs } from "react-merge-refs";
import clsx from "clsx";

import { Icon } from "../icon";

import type { IDropdownSearchProps } from "./types";
import classes from "./styles.module.scss";

import { BaseInput } from "@/components/formControl/baseInput/baseInput";
import { useDebounced } from "@/hooks/useDebounced";
import { InputIcon } from "@/components/formControl/inputIcon";

export const DropdownSearch = memo(
  forwardRef(function DropdownSearch(
    props: IDropdownSearchProps,
    ref: Ref<HTMLInputElement>,
  ) {
    const {
      value,
      onValueChange,
      onDebouncedChange,
      placeholder,
      containerClassName,
      ...rest
    } = props;
    const { t } = useTranslation("component");

    const inputRef = useRef<HTMLInputElement>(null);

    const debouncedValue = useDebounced(value?.toString());

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onValueChange?.(event.target.value || "");
      },
      [onValueChange],
    );

    const handleClick = useCallback(() => {
      onValueChange?.("");
      onDebouncedChange?.("");
    }, [onValueChange, onDebouncedChange]);

    useEffect(() => {
      if (debouncedValue !== undefined) onDebouncedChange?.(debouncedValue);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    return (
      <div
        onMouseDown={(event) => {
          event.preventDefault();
          inputRef.current?.focus();
        }}
        className={clsx([classes.containerFormControl, containerClassName])}
      >
        <Icon className={"icon"} name={"Search2Line"} fontSize={"fs-20"} />
        <BaseInput
          onMouseDown={(event) => {
            event.stopPropagation();
          }}
          onChange={handleChange}
          ref={mergeRefs([inputRef, ref])}
          value={value}
          placeholder={placeholder || t("dropdownSearch.placeholder")}
          {...rest}
        />
        <InputIcon onClick={handleClick} />
      </div>
    );
  }),
);

DropdownSearch.displayName = "DropdownSearch";
